import React, {ChangeEvent, useEffect, useMemo, useState} from 'react';
import FormControlFactory from './FormControlFactory';
import {useMobile} from "../../../context/MobileContext";
import {ImageInput, ImageValue, TextInput, rowLabel} from "../../../models/Template";
import {SelectedImageData, useTemplate} from "../../../context/TemplateContext";
import {styled} from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import ViewListIcon from '@mui/icons-material/ViewList';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {useTranslation} from "react-i18next";
import {FormControlLabel, Grid, IconButton, Switch} from '@mui/material';
import {Toggle} from "../../../scene/rowVisibility";
import {useRows} from "../rows/useRows";
import {buildFillFields, rowSummary} from "../FillMode/fieldList";

interface InputControlProps {
    selectedDesignKey: string;
    /** Index of the design on screen — rows and switches are read off its scene. */
    selectedDesign: number;
    inputs: {
        [key: string]: ImageInput | TextInput;
    };
    selections: {
        [key: string]: string | ImageValue
    };
    handleImageClick: (imageData: SelectedImageData) => void;
    handleChangeText: (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, key: string) => void;
    handleImageTransform: (key: string | undefined) => void;
    handleResetImageTransform: (key: string, value: ImageValue) => void;
    /** Nudge a bound text element's font size ±10%. Owned by TemplateEditor so
     *  ONE implementation (the recursive scene lookup, on the MERGED scene)
     *  serves the desktop panel, Fill Mode and user layers alike — the flat
     *  lookup that used to live here missed nested text and every layer. */
    handleFontStep: (key: string, direction: 1 | -1) => void;
    textAccordionOpen: boolean;
    setTextAccordionOpen: (open: boolean) => void;
    imageAccordionOpen: boolean;
    setImageAccordionOpen: (open: boolean) => void;
    /** Row reposition: move the whole row on the canvas. Pressing it on the row
     *  already being moved exits (TemplateEditor.handleRowReposition toggles). */
    onRowReposition?: (row: string) => void;
}

type InputsObject = {
    text: {
        key: string;
        input: TextInput;
    }[]
    image: {
        key: string;
        input: ImageInput;
    }[]
}

// Styled components
const AccordionHeader = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    color: 'white',
    borderRadius: '4px 4px 0 0',
    cursor: 'pointer',
    borderBottom: '1px solid #ffffff70'
}));

const AccordionContent = styled(Box)(({theme}) => ({
    padding: '16px 0 0',
    borderRadius: '0 0 4px 4px',
}));

const ExpandIcon = styled(ExpandMoreIcon, {
    shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({open}) => ({
    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.3s ease',
}));

const AccordionContainer = styled(Box)(({theme}) => ({}));

const HeaderTitle = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
}));

/** Shared look for the small action links in a text row's top-right corner. */
const textActionSx = {
    background: 'transparent',
    border: 'none',
    color: '#C3F3FD',
    cursor: 'pointer',
    fontSize: '0.8rem',
    padding: '4px 2px',
    textDecoration: 'underline',
} as const;

/** Tiny action links rendered in the top-right of each text input row:
 *  font-size minus/plus steppers and "Reposition". Reposition puts the scene
 *  editor into the same transform flow image inputs use, with the Konva
 *  Transformer attached to the bound text element; while active it morphs
 *  into a "Done" link. The font steppers nudge the bound text element's
 *  size ±10% per click via a sceneTransforms fontSize override — the same
 *  store the transform flow writes, so the export honors it identically.
 *  (The image-input version of this control is inside ImageSelector.tsx.) */
const TextRowActions: React.FC<{
    elementKey: string;
    isRepositioning: boolean;
    onReposition: (key: string | undefined) => void;
    onFontStep?: (key: string, direction: 1 | -1) => void;
}> = ({elementKey, isRepositioning, onReposition, onFontStep}) => {
    const {t} = useTranslation();
    return (
        <Box sx={{position: 'absolute', top: 0, right: 0, display: 'flex', gap: '6px', zIndex: 1}}>
            {onFontStep && (
                <>
                    <Box
                        component="button"
                        type="button"
                        title={t('editor.imageSelector.fontDecrease')}
                        onClick={() => onFontStep(elementKey, -1)}
                        sx={textActionSx}
                    >
                        {t('editor.imageSelector.fontDecrease')}
                    </Box>
                    <Box
                        component="button"
                        type="button"
                        title={t('editor.imageSelector.fontIncrease')}
                        onClick={() => onFontStep(elementKey, 1)}
                        sx={textActionSx}
                    >
                        {t('editor.imageSelector.fontIncrease')}
                    </Box>
                </>
            )}
            <Box
                component="button"
                type="button"
                onClick={() => onReposition(isRepositioning ? undefined : elementKey)}
                sx={textActionSx}
            >
                {t(isRepositioning ? 'editor.imageSelector.confirm' : 'editor.imageSelector.reposition')}
            </Box>
        </Box>
    );
};


const InputControls: React.FC<InputControlProps> = ({
                                                        selectedDesignKey,
                                                        selectedDesign,
                                                        inputs,
                                                        selections,
                                                        handleImageClick,
                                                        handleChangeText,
                                                        handleImageTransform,
                                                        handleResetImageTransform,
                                                        handleFontStep,
                                                        textAccordionOpen,
                                                        setTextAccordionOpen,
                                                        imageAccordionOpen,
                                                        setImageAccordionOpen,
                                                        onRowReposition,
                                                    }) => {
    const {selectedTransformedImage, template, repositioningRow} = useTemplate();
    // Reposition for TEXT inputs only makes sense in the scene engine — the
    // legacy HTML editor has no positioned-text transform UI to drive the
    // Konva.Transformer. Find the currently active design from the key.
    const activeScene = template?.designs?.find((d) => d.key === selectedDesignKey)?.scene;
    const isSceneDesign = !!activeScene;

    const [selectedImageData, setSelectedImageData] = useState<SelectedImageData | undefined>(undefined);
    const [adaptedInputs, setAdaptedInputs] = useState<InputsObject>({
        text: [],
        image: []
    });
    const {isMobile} = useMobile();
    const {t} = useTranslation();
    const rows = useRows(selectedDesign);
    // Rows fold open independently; all start closed so a six-row schedule
    // doesn't push the rest of the panel off screen.
    const [openRows, setOpenRows] = useState<ReadonlySet<string>>(new Set());
    const [optionsOpen, setOptionsOpen] = useState(true);
    // Only for the row summaries: Fill Mode's list reads in board order, so a
    // row's "home · away · time" reads the way the design does.
    const fillFields = useMemo(() => buildFillFields(inputs, activeScene), [inputs, activeScene]);

    const toggleTextAccordion = () => setTextAccordionOpen(!textAccordionOpen);
    const toggleImageAccordion = () => setImageAccordionOpen(!imageAccordionOpen);

    const handleImageInputClick = (imageData: SelectedImageData) => {
        handleImageClick(imageData);
    }

    function filterTemplates() {
        const _adaptedInputs: InputsObject = {
            text: [],
            image: []
        }
        // The admin's fill order sorts WITHIN each accordion. Desktop keeps its
        // text/image split, so an INTERLEAVED order (text, image, text) cannot be
        // shown faithfully here — Fill Mode honours that exactly. Absent order
        // leaves the historical type-then-placeholder sort untouched, so no
        // existing template shifts.
        const fillOrder = template?.fillOrder;
        Object.keys(inputs)
            .filter(key => inputs[key].editable)
            .sort((a, b) => {
                if (fillOrder && fillOrder.length > 0) {
                    const orderA = fillOrder.indexOf(a);
                    const orderB = fillOrder.indexOf(b);
                    if (orderA !== -1 && orderB !== -1) return orderA - orderB;
                    if (orderA !== -1) return -1;
                    if (orderB !== -1) return 1;
                }
                const typeA = inputs[a].type;
                const typeB = inputs[b].type;
                const placeholderA = inputs[a].placeholder;
                const placeholderB = inputs[b].placeholder;

                // First, sort by type
                if (typeA !== typeB) {
                    return typeA === 'text' ? -1 : typeB === 'text' ? 1 : 0;
                }

                // If types are the same, sort by placeholder
                if (placeholderA < placeholderB) return -1;
                if (placeholderA > placeholderB) return 1;
                return 0;
            })
            .forEach((key, index, array) => {
                const input = inputs[key];

                if (input.type === 'image') {
                    _adaptedInputs.image.push({key, input})
                } else if (input.type === 'text') {
                    _adaptedInputs.text.push({key, input})
                }
            })

        setAdaptedInputs(_adaptedInputs)
    }

    // update the selected image data in case of a change in the template
    useEffect(() => {
        filterTemplates()

        if (selectedImageData && inputs[selectedImageData.elementKey]?.type == 'image') {
            const key = selectedImageData.elementKey;
            const newValue = inputs[key] as ImageInput
            setSelectedImageData({
                elementKey: key,
                label: newValue.placeholder,
                value: newValue.default,
                folderId: newValue.folderId!,
            })
        }
        // fillOrder is read by filterTemplates above, so it MUST be a dependency:
        // the context can hand back a new order with the same `inputs` reference,
        // and the panel would keep showing the previous sequence.
    }, [inputs, template?.fillOrder])


    const renderText = ({key, input}: InputsObject['text'][number]) => {
        const commonProps: any = {
            elementKey: key,
            label: input.placeholder,
            value: selections[key] || ''
        };

        const editor = input.inputType == 'textarea' ? (
            <FormControlFactory key={key} type="textarea"
                                selectedDesignKey={selectedDesignKey}
                                onChange={handleChangeText} {...commonProps} />
        ) : (
            <FormControlFactory key={key} type="text"
                                selectedDesignKey={selectedDesignKey}
                                onChange={handleChangeText} {...commonProps}
                                maxChars={input.maxChars ?? 100}/>
        );

        return (
            <Box key={key} sx={{position: 'relative'}}>
                {/* Font +/- and Reposition links, mirror of the image
                    input's button. Scene-engine only (legacy HTML has no
                    positioned-text transform). Rendered as small text
                    links aligned to the opposite side of the input label. */}
                {isSceneDesign && (
                    <TextRowActions
                        elementKey={key}
                        isRepositioning={selectedTransformedImage === key}
                        onReposition={handleImageTransform}
                        onFontStep={handleFontStep}
                    />
                )}
                {editor}
            </Box>
        );
    };

    const renderImages = (images: InputsObject['image']) => (
        <Grid container spacing={3}>
            {images.map(inputObject => {
                const {key, input} = inputObject;
                const commonProps: any = {
                    elementKey: key,
                    label: input.placeholder,
                    value: selections[key]
                };
                return selections[key] && // we need to wait until the selection exists
                    <FormControlFactory key={key}
                                        type="image"
                                        selectedDesignKey={selectedDesignKey}
                                        onClick={handleImageInputClick}
                                        onImageTransformResetClick={handleResetImageTransform}
                                        onImageTransformClick={handleImageTransform}
                                        folderId={input.folderId}
                                        allowImageTransform={input?.size?.[selectedDesignKey]?.width != undefined && input?.size?.[selectedDesignKey]?.height != undefined}
                                        {...commonProps} />;
            })}
        </Grid>
    );

    const renderSwitch = (toggle: Toggle) => (
        <FormControlLabel
            key={toggle.key}
            sx={{display: 'flex', color: 'white', mb: 1}}
            control={<Switch checked={rows.isOn(toggle)} onChange={(_, on) => rows.setToggle(toggle, on)}/>}
            label={toggle.label}
        />
    );

    // A field switched off with its layer is not on the design: nothing to edit.
    const shown = <T extends { key: string }>(list: T[], row?: string) =>
        list.filter(({key}) => rows.rowOf.get(key) === row && !rows.off.has(key));
    const text = shown(adaptedInputs.text);
    const images = shown(adaptedInputs.image);
    const options = rows.toggles.filter((toggle) => !toggle.row);

    return (
        <>
            {/* Text Inputs Accordion */}
            {text.length > 0 && <AccordionContainer>
                <AccordionHeader onClick={toggleTextAccordion}>
                    <HeaderTitle>
                        <EditIcon sx={{marginRight: 1}}/>
                        <Typography variant="subtitle1">{t('editor.title.editText')}</Typography>
                    </HeaderTitle>
                    <ExpandIcon open={textAccordionOpen}/>
                </AccordionHeader>

                {textAccordionOpen && (
                    <AccordionContent>
                        {text.map(renderText)}
                    </AccordionContent>
                )}
            </AccordionContainer>}

            {/* Image Inputs Accordion */}
            {images.length > 0 && <AccordionContainer>
                <AccordionHeader onClick={toggleImageAccordion}>
                    <HeaderTitle>
                        <ImageIcon sx={{marginRight: 1}}/>
                        <Typography variant="subtitle1">{t('editor.title.editImages')}</Typography>
                    </HeaderTitle>
                    <ExpandIcon open={imageAccordionOpen}/>
                </AccordionHeader>

                {imageAccordionOpen && (
                    <AccordionContent>
                        {renderImages(images)}
                    </AccordionContent>
                )}
            </AccordionContainer>}

            {/* Template-wide switches (Template.toggles outside any row). */}
            {options.length > 0 && <AccordionContainer>
                <AccordionHeader onClick={() => setOptionsOpen(!optionsOpen)}>
                    <HeaderTitle>
                        <TuneIcon sx={{marginRight: 1}}/>
                        <Typography variant="subtitle1">{t('editor.rows.options')}</Typography>
                    </HeaderTitle>
                    <ExpandIcon open={optionsOpen}/>
                </AccordionHeader>
                {optionsOpen && <AccordionContent>{options.map(renderSwitch)}</AccordionContent>}
            </AccordionContainer>}

            {/* One accordion per repeated row; a hidden row stays listed (its
                values are kept) with the eye to bring it back. */}
            {rows.keys.map((row) => {
                const hidden = rows.hidden.has(row);
                const open = openRows.has(row);
                const rowText = shown(adaptedInputs.text, row);
                const rowImages = shown(adaptedInputs.image, row);
                const summary = rowSummary(fillFields.filter((f) => rows.rowOf.get(f.key) === row), selections);
                return (
                    <AccordionContainer key={row} data-testid={`row-accordion-${row}`}>
                        <AccordionHeader onClick={() => setOpenRows((prev) => {
                            const next = new Set(prev);
                            if (!next.delete(row)) next.add(row);
                            return next;
                        })}>
                            <HeaderTitle sx={{minWidth: 0, gap: 1, color: hidden ? '#7d97aa' : 'white'}}>
                                <ViewListIcon sx={{marginRight: 1}}/>
                                <Typography variant="subtitle1" noWrap sx={{flexShrink: 0}}>{rowLabel(template, row)}</Typography>
                                <Typography variant="body2" dir="auto" noWrap sx={{color: '#8fb0c8'}}>{summary}</Typography>
                            </HeaderTitle>
                            <Box sx={{display: 'flex', alignItems: 'center', flexShrink: 0}}>
                                {/* Move the whole row on the canvas. A hidden row
                                    has nothing on the board to move. */}
                                {isSceneDesign && !hidden && onRowReposition && (
                                    <IconButton
                                        size="small"
                                        title={t('editor.rows.reposition')}
                                        aria-label={t('editor.rows.reposition')}
                                        aria-pressed={repositioningRow === row}
                                        data-testid={`row-reposition-${row}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRowReposition(row);
                                        }}
                                        sx={repositioningRow === row
                                            ? {color: 'white', backgroundColor: '#00A1E1', '&:hover': {backgroundColor: '#00A1E1'}}
                                            : {color: '#C3F3FD'}}
                                    >
                                        <OpenWithIcon fontSize="small"/>
                                    </IconButton>
                                )}
                                <IconButton
                                    size="small"
                                    title={t(hidden ? 'editor.rows.show' : 'editor.rows.hide')}
                                    aria-label={t(hidden ? 'editor.rows.show' : 'editor.rows.hide')}
                                    data-testid={`row-eye-${row}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (hidden) rows.show(row);
                                        else rows.hide(row);
                                    }}
                                    sx={{color: '#C3F3FD'}}
                                >
                                    {hidden ? <VisibilityIcon fontSize="small"/> : <VisibilityOffIcon fontSize="small"/>}
                                </IconButton>
                                <ExpandIcon open={open}/>
                            </Box>
                        </AccordionHeader>
                        {open && (
                            <AccordionContent>
                                {rowText.map(renderText)}
                                {rowImages.length > 0 && <Box sx={{mb: 2}}>{renderImages(rowImages)}</Box>}
                                {rows.toggles.filter((toggle) => toggle.row === row).map(renderSwitch)}
                            </AccordionContent>
                        )}
                    </AccordionContainer>
                );
            })}

        </>
    );
};

export default InputControls;